import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { GitCredential, GitRoutingRule } from '@/types/gitRouting'

export interface RemoveCredentialDialogProps {
  credential?: GitCredential
  /** The tenant rules that still name it; removal is refused while there are any. */
  usedBy: GitRoutingRule[]
  isRemoving?: boolean
  onClose: () => void
  onConfirm: () => void
}

/**
 * Remove one of the tenant's tokens (GR6). Not offered while a rule still names it: the rule would go on
 * matching and every call it routes would fail to authenticate.
 */
export function RemoveCredentialDialog({ credential, usedBy, isRemoving, onClose, onConfirm }: RemoveCredentialDialogProps) {
  const blocked = usedBy.length > 0
  const rules = usedBy.map((r) => `rule ${r.position}`).join(', ')

  return (
    <AlertDialog open={credential !== undefined} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove token {credential?.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            {blocked
              ? `Not possible while ${rules} ${usedBy.length === 1 ? 'uses' : 'use'} it. Delete or re-point ${usedBy.length === 1 ? 'that rule' : 'those rules'} first.`
              : 'The token is deleted from the credential store and cannot be recovered. Anything still routed to this name stops authenticating.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {blocked ? (
            <AlertDialogCancel>Close</AlertDialogCancel>
          ) : (
            <>
              <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={isRemoving}
                onClick={onConfirm}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isRemoving ? 'Removing…' : 'Remove'}
              </AlertDialogAction>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
